/**
 * StatCard - the canonical metric/stat card (design treatment "V1").
 * Clean hairline card + one soft shadow; colour lives in a soft-tinted icon
 * tile, never a border stripe. Eyebrow label + tabular-monospace value.
 * Reused everywhere so stat cards stay identical across the app.
 */

import React from 'react';
import { Card, CardContent, Box, Typography } from '@mui/material';
import { useThemeMode, numericSx, eyebrowSx, radius } from '../../context/ThemeContext';

const tone = (palette, t) => ({
  accent:  { c: palette.accent,  bg: palette.accentBg },
  info:    { c: palette.info,    bg: palette.infoBg },
  teal:    { c: palette.teal,    bg: palette.tealBg },
  warning: { c: palette.warning, bg: palette.warningBg },
  danger:  { c: palette.danger,  bg: palette.dangerBg },
  purple:  { c: palette.purple,  bg: palette.purpleBg },
  neutral: { c: palette.textSecondary, bg: palette.surfaceHover },
}[t] || { c: palette.accent, bg: palette.accentBg });

function StatCard({ label, value, sub, icon, tone: toneKey = 'accent', valueColor, onClick, sx }) {
  const { palette } = useThemeMode();
  const t = tone(palette, toneKey);

  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': onClick ? { boxShadow: palette.shadowMd, borderColor: palette.accent } : undefined,
        ...sx,
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 1 }}>
          <Typography sx={{ ...eyebrowSx, color: palette.textMuted, mt: 0.5 }}>{label}</Typography>
          {icon && (
            <Box sx={{
              width: 38, height: 38, flexShrink: 0,
              borderRadius: `${radius.sm}px`,
              backgroundColor: t.bg, color: t.c,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              '& svg': { fontSize: 20 },
            }}>
              {icon}
            </Box>
          )}
        </Box>
        <Typography sx={{ ...numericSx, fontWeight: 700, fontSize: '1.75rem', lineHeight: 1.05, color: valueColor || palette.text }}>
          {value}
        </Typography>
        {sub && (
          <Box sx={{ mt: 0.75, fontSize: '0.72rem', color: palette.textMuted }}>
            {sub}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default StatCard;
